import { Button, TextField } from '@mgh-app/component-library';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { storeSignUpData } from 'slices/SignUpSlice';
import type { SignUpError } from 'services/UsersService';
import { useLazySignUpQuery } from 'services/UsersService';
import { useAppDispatch } from 'store';
import { display, flexDirection, fontSize, margin, textColor, twCls } from 'style';
import { EmailUtils } from 'utils/EmailUtils';

interface Fields {
  registrationCode: string;
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type ErrorTypes = 'required' | 'pattern' | 'validate';

const ErrorHelper = {
  registrationCode: {
    required: 'Registration Code is required',
    pattern: '',
    validate: '',
  },
  fullName: {
    required: 'Full Name is required',
  },
  email: {
    required: 'Email Address is required',
    pattern: 'Email Address is incorrect',
    validate: '',
  },
  password: {
    required: 'Password is required',
  },
  confirmPassword: {
    required: 'Confirm Password is required',
    pattern: '',
    validate: 'Password and Confirm Password should match',
  },
};

export const Registration = (): JSX.Element => {
  const { setTitle, showNavigationButtons, showUserProfile, navigateHomeAction, showHomeButton } =
    useContext(MainLayoutContext);
  const { handleSubmit, control, getValues } = useForm<Fields>({
    defaultValues: {
      registrationCode: '',
      email: '',
      fullName: '',
      password: '',
      confirmPassword: '',
    },
  });
  const [customError, setCustomError] = useState<string>();
  const { navigateLogin, navigateVerifyAccount } = useAppNavigation();
  const [signUp] = useLazySignUpQuery();
  const dispatch = useAppDispatch();

  const navigateHome = useCallback(() => {
    navigateLogin();
  }, [navigateLogin]);

  useEffect(() => {
    setTitle('Register Account');
    showNavigationButtons(true);
    showUserProfile(false);
    showHomeButton(false);
    navigateHomeAction(() => navigateHome);
  }, [
    navigateHome,
    navigateHomeAction,
    setTitle,
    showHomeButton,
    showNavigationButtons,
    showUserProfile,
  ]);

  const register = async ({ email, password, fullName, registrationCode }: Fields) => {
    try {
      await signUp({
        email,
        password,
        name: fullName,
        auth_code: registrationCode,
      }).unwrap();
      dispatch(storeSignUpData({ email, password }));
      navigateVerifyAccount();
    } catch (err) {
      const error = err as SignUpError;
      if (error.message) {
        if (Array.isArray(error?.message)) {
          setCustomError(error.message.join(', '));
        } else if (error?.message) {
          setCustomError(error.message);
        } else {
          setCustomError('Unknown error');
        }
      } else {
        console.error(err);
      }
    }
  };

  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'))}>
      <Controller
        name="registrationCode"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={ErrorHelper.registrationCode[error?.type as ErrorTypes]}
            className={twCls(margin('mt-14'))}
            label="Your unique 10 digit authentication code"
            {...field}
          />
        )}
      />
      <Controller
        name="fullName"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={error?.type === 'required' && ErrorHelper.fullName.required}
            className={twCls(margin('mt-11'))}
            label="Full Name"
            {...field}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: EmailUtils.EmailRegex,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={error?.type === 'required' && ErrorHelper.email.required}
            className={twCls(margin('mt-11'))}
            label="Email Address"
            {...field}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={error?.type === 'required' && ErrorHelper.password.required}
            className={twCls(margin('mt-11'))}
            label="Password"
            type="password"
            {...field}
          />
        )}
      />
      <Controller
        name="confirmPassword"
        control={control}
        rules={{
          required: true,
          validate: (value) => value === getValues().password,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={ErrorHelper.confirmPassword[error?.type as ErrorTypes]}
            className={twCls(margin('mt-11'))}
            label="Confirm Password"
            type="password"
            {...field}
          />
        )}
      />
      <p className={twCls(textColor('text-red-100'), fontSize('text-xs'), margin('mt-10'))}>
        {customError || <>&nbsp;</>}
      </p>
      <Button className={twCls(margin('mt-4'))} onClick={handleSubmit(register)}>
        Register
      </Button>
    </div>
  );
};
